"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, ArrowRight, PhoneCall, CircleCheck as CheckCircle2, Wrench, Droplets, Building2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
export default function WhyChoose() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = ["No subcontractors or handoffs between demo and rebuild", "Direct billing and documentation with your insurance adjuster", "24/7 direct local dispatch across MD, DC, and Northern Virginia", "Certified hydroxyl and ozone molecular odor elimination"];
  const relatedServices = [{
    title: "Reconstruction & Design-Build",
    description: "Seamless transition from soot removal to structural carpentry, drywall, framing, and complete interior restoration.",
    to: "/ReconstructionDesignBuildPage",
    icon: Wrench,
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_design_build.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_design_build.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_design_build.png-wvc-sizes",
    alt: "Restoration crew completing framing and drywall rebuild"
  }, {
    title: "Water Damage & Flood Restoration",
    description: "Rapid water extraction, commercial dehumidification, and moisture telemetry following fire suppression efforts.",
    to: "/WaterDamageFloodRestorationPage",
    icon: Droplets,
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_flood_restoration.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_flood_restoration.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_flood_restoration.png-wvc-sizes",
    alt: "Industrial dehumidifiers and moisture extractors operating on site"
  }, {
    title: "Commercial & HOA Property Restoration",
    description: "Multi-unit containment, fire partition repair, and tenant coordination for residential complexes and commercial facilities.",
    to: "/CommercialHOAPropertyRestorationPage",
    icon: Building2,
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_restoration.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_restoration.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/commercial_hoa_restoration.png-wvc-sizes",
    alt: "Commercial property restoration team assessing structural fire impact"
  }];
  return <section id="why-choose-triserve-related-services" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 border-t border-border/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Deep Purple Structural Hero Container */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground border border-border/20 p-6 sm:p-10 lg:p-14 mb-16 lg:mb-20 overflow-hidden shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Narrative Column */}
            <motion.div className="lg:col-span-7 space-y-6" initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              {/* Technical Spec Overline */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-foreground/10 border border-secondary-foreground/20 text-xs font-semibold tracking-wider uppercase font-mono">
                <ShieldCheck className="w-4 h-4 text-accent" />
                <span>Single Contractor Accountability</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground font-default leading-tight">
                One local crew from initial cleanup through final rebuild
              </h2>

              <p className="text-secondary-foreground/90 text-base sm:text-lg leading-relaxed">
                Most disaster companies handle soot removal and then pass your home to third-party remodelers. TriServe maintains certified mitigation specialists and licensed master builders under one roof across Maryland, DC, and Northern Virginia.
              </p>

              <p className="text-secondary-foreground/80 text-sm sm:text-base leading-relaxed">
                By keeping project management unified, we eliminate misaligned estimates, overlapping deductibles, and delays between emergency stabilization and finish carpentry. You speak directly to the local project director working your claim from the day the trucks arrive.
              </p>

              {/* Concrete Benefit Points */}
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
                {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm text-secondary-foreground/90 leading-snug">{benefit}</span>
                  </li>)}
              </ul>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-4">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold border border-accent/40 shadow-md">
                  <Link to="/ContactRequestServicePage">
                    Request emergency service
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="bg-transparent text-secondary-foreground border-secondary-foreground/30 hover:bg-secondary-foreground/10 hover:text-secondary-foreground">
                  <a href="tel:855-435-6631" className="flex items-center gap-2 font-mono">
                    <PhoneCall className="w-4 h-4 text-accent" />
                    <span>855-435-6631</span>
                  </a>
                </Button>
              </div>
            </motion.div>

            {/* Right Photo Column: Industrial Hydroxyl Odor Neutralizer */}
            <motion.div className="lg:col-span-5" initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: 0.1,
            ease: "easeOut"
          }}>
              <div className="relative rounded-xl overflow-hidden border border-secondary-foreground/20 shadow-xl bg-background/5">
                <div className="aspect-[4/3] sm:aspect-[16/11] w-full overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/hydroxyl_odor_neutralizer_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/hydroxyl_odor_neutralizer_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/hydroxyl_odor_neutralizer_technician.png-wvc-sizes" alt="TriServe restoration technician operating an industrial hydroxyl odor neutralizer on site" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                </div>
                
                {/* Equipment telemetry badge */}
                <div className="p-3.5 bg-background/95 text-foreground border-t border-border flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary inline-block animate-pulse" />
                    <span className="font-mono font-medium text-foreground">Hydroxyl Molecular Treatment</span>
                  </div>
                  <span className="text-muted-foreground font-mono text-[11px]">Zero Secondary Residue</span>
                </div>
              </div>
            </motion.div>

          </div>
        </div>

        {/* Section Cross-Links Heading */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-mono font-semibold text-primary mb-2">
              <span>Integrated Restoration Capabilities</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
              Related emergency restoration services
            </h3>
          </div>
          <p className="text-muted-foreground text-sm max-w-md">
            Fire and smoke events often introduce secondary water damage and structural framing requirements. Explore our specialized services.
          </p>
        </div>

        {/* Cross-Link Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {relatedServices.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: i * 0.08,
            ease: "easeOut"
          }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full overflow-hidden bg-card text-card-foreground border-border hover:border-primary/60 transition-[border-color,box-shadow] duration-200 hover:shadow-lg">
                  <div>
                    <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                      <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      <div className="absolute top-3 left-3 p-2 rounded-md bg-background/90 text-primary border border-border shadow-xs">
                        <IconComponent className="w-5 h-5" />
                      </div>
                    </div>

                    <CardHeader className="pt-5 pb-2">
                      <CardTitle className="text-lg sm:text-xl font-bold font-default leading-snug">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 pb-6">
                    <Button asChild variant="outline" className="w-full justify-between font-semibold group hover:bg-primary hover:text-primary-foreground transition-colors">
                      <Link to={service.to}>
                        <span>Explore service</span>
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-150 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

      </div>
    </section>;
}